import type { ChartDataModel } from '@chartforge/shared-types';

/**
 * Data validation for the timeline chart. Pure logic — unit tested without
 * any DOM.
 */
export function validateTimelineModel(model: ChartDataModel): {
  ok: boolean;
  errors?: string[];
} {
  const errors: string[] = [];

  if (!model || typeof model !== 'object') {
    return { ok: false, errors: ['Chart data model is required'] };
  }

  if (!Array.isArray(model.categories) || model.categories.length === 0) {
    errors.push('At least one category is required');
  }
  if (!Array.isArray(model.series) || model.series.length === 0) {
    errors.push('At least one series is required');
  }

  const categories = new Set(Array.isArray(model.categories) ? model.categories : []);
  const seenIds = new Set<string>();

  for (const series of model.series ?? []) {
    const name = series.name || series.id || '(unnamed)';

    if (!series.id) {
      errors.push(`Series "${name}" is missing an id`);
    } else if (seenIds.has(series.id)) {
      errors.push(`Duplicate series id "${series.id}"`);
    } else {
      seenIds.add(series.id);
    }

    if (series.type !== 'timeline') {
      errors.push(
        `Series "${name}" has type "${String(series.type)}"; the timeline chart requires type "timeline"`
      );
    }

    if (!Array.isArray(series.points) || series.points.length === 0) {
      errors.push(`Series "${name}" has no data points`);
      continue;
    }

    for (const point of series.points) {
      if (typeof point.value !== 'number' || !Number.isFinite(point.value)) {
        errors.push(`Series "${name}": point at "${point.category}" has a non-finite value`);
      }
      if (!categories.has(point.category)) {
        errors.push(`Series "${name}": unknown category "${point.category}"`);
      }
    }
  }

  return errors.length > 0 ? { ok: false, errors } : { ok: true };
}
